import { CELL_LABELS, type Cell, type PlanView } from "../plan";

type Props = {
  plan: PlanView;
  variant?: "full" | "mini" | "sample" | "stage";
  screen?: boolean;
};

const LEGEND: { cell: Cell; label: string }[] = [
  { cell: "picked", label: "Proposée" },
  { cell: "free", label: "Libre" },
  { cell: "sold", label: "Occupée" },
  { cell: "pmr", label: "PMR" },
  { cell: "blocked", label: "Indispo." },
];

export function SeatPlan({ plan, variant = "full", screen = true }: Props) {
  // Pathé ne donne aucune dimension d'écran : on le cale sur la largeur des sièges.
  const used = plan.rows.flatMap((row) =>
    row.cells.map((cell, column) => (cell === "empty" ? -1 : column)).filter((column) => column >= 0),
  );
  const span = used.length > 0 ? Math.max(...used) - Math.min(...used) + 1 : plan.columns;
  const screenWidth = `calc(${span} * (var(--seat) + var(--seat-gap)) - var(--seat-gap))`;
  const withNames = variant === "full";
  const wrapper =
    variant === "full" ? "plan" : variant === "stage" ? "plan stage-plan-grid" : "plan mini-plan";

  return (
    <>
      <div className={wrapper}>
        <div className="plan-grid">
          {plan.rows.map((row, rowIndex) =>
            row.gap ? (
              <div className="plan-gap" key={`gap-${rowIndex}`} aria-hidden="true" />
            ) : (
            <div
              className="plan-row"
              key={row.name ?? `r${rowIndex}`}
              style={{ "--columns": plan.columns } as React.CSSProperties}
            >
              {variant !== "sample" && variant !== "stage" && (
                <span className="plan-row-name">{row.name}</span>
              )}
              {row.cells.map((cell, columnIndex) => (
                <span
                  key={columnIndex}
                  className={`cell is-${cell}${row.marks?.[columnIndex] ? ` ${row.marks[columnIndex]}` : ""}`}
                  title={
                    withNames && cell !== "empty"
                      ? `${row.name}${columnIndex + 1} — ${CELL_LABELS[cell]}`
                      : undefined
                  }
                />
              ))}
            </div>
            ),
          )}
        </div>
      </div>

      {screen && variant === "full" && (
        <div className="screen">
          <div className="screen-glow" style={{ width: screenWidth }} />
          <span className="screen-label">ÉCRAN</span>
        </div>
      )}

      {screen && variant === "stage" && (
        <div className="screen">
          <div className="screen-glow" style={{ width: screenWidth }} />
          <span className="screen-label">ÉCRAN</span>
        </div>
      )}

      {screen && variant === "mini" && (
        <div className="mini-screen">
          <i />
          <span>ÉCRAN</span>
          <i />
        </div>
      )}
    </>
  );
}

export function PlanLegend({ withPicked = false }: { withPicked?: boolean }) {
  return (
    <ul className="legend">
      {LEGEND.filter((entry) => withPicked || entry.cell !== "picked").map((entry) => (
        <li key={entry.cell}>
          <span className={`cell is-${entry.cell}`} />
          {entry.label}
        </li>
      ))}
    </ul>
  );
}
